import { useState, useEffect } from "react";
import { ArrowRight, BrainCircuit, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/hooks/use-session";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

// Generate a large pool of market context questions focused on specific countries, assets, and sectors
const generateMarketContextQuestions = () => {
  return [
    // Asset-specific questions
    "What's the forecast for AAPL stock in the next quarter?",
    "How will Tesla stock perform with recent EV market changes?",
    "What factors are driving Bitcoin price movements this month?",
    "Should I invest in NVIDIA given AI chip demand?",
    "What's the outlook for gold prices with current inflation trends?",
    "How is Amazon stock likely to perform this quarter?",
    "What's the forecast for Microsoft stock with AI developments?",
    "How are interest rates affecting bank stocks like JPMorgan?",
    
    // Country-specific questions
    "How are Japan's monetary policies affecting the Nikkei index?",
    "What impact will China's economic data have on global markets?",
    "How is the UK economy performing post-Brexit?",
    "What's the outlook for German manufacturing sector?",
    "How are India's tech stocks performing this quarter?",
    "What impact will US inflation have on the dollar?",
    
    // Sector/category questions
    "What are the key factors influencing the S&P 500 this week?",
    "Which energy stocks will benefit from current oil prices?",
    "How is the semiconductor sector performing against market trends?",
    "What's the outlook for REITs with current interest rates?",
    "Which healthcare stocks show the most promise this year?",
    "How will AI affect the tech sector in the next quarter?"
  ];
};

interface AskStrykrAICardProps {
  className?: string;
  onQuestionClick?: () => void;
}

const AskStrykrAICard = ({ 
  className, 
  onQuestionClick 
}: AskStrykrAICardProps) => {
  const navigate = useNavigate();
  const { session } = useSession();
  const [allQuestions] = useState(generateMarketContextQuestions());
  const [visibleQuestions, setVisibleQuestions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const questionsPerPage = 5;
  
  // Set up visible questions based on current page
  useEffect(() => {
    const startIdx = currentPage * questionsPerPage;
    setVisibleQuestions(allQuestions.slice(startIdx, startIdx + questionsPerPage));
  }, [currentPage, allQuestions]);
  
  // Rotate questions every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % Math.ceil(allQuestions.length / questionsPerPage));
    }, 30000);
    return () => clearInterval(interval);
  }, [allQuestions.length]);
  
  // Handle manual navigation
  const nextPage = () => {
    setCurrentPage(prev => (prev + 1) % Math.ceil(allQuestions.length / questionsPerPage));
  };
  
  const prevPage = () => {
    setCurrentPage(prev => 
      prev === 0 ? Math.ceil(allQuestions.length / questionsPerPage) - 1 : prev - 1
    );
  };

  // Function to fetch user query limit and profile
  const fetchUserProfile = async () => {
    if (!session?.user?.id) return null;
    
    const { data, error } = await supabase
      .from('profiles')
      .select('subscription_status')
      .eq('id', session.user.id)
      .single();
      
    if (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
    return data;
  };

  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['userProfile', session?.user?.id],
    queryFn: fetchUserProfile,
    enabled: !!session?.user?.id,
  });

  const handleQuestionClick = (question: string) => {
  sessionStorage.setItem("strykr-initial-question", question);
    // If we need to handle limitations before navigating:
    // if (userProfile?.subscription_status !== 'active' && userProfile?.query_limit <= 0) {
    //   // Show upgrade prompt or similar
    //   console.log("Query limit reached for non-active subscription.");
    //   return;
    // }
    
    // Pass question as state to the chat page
    navigate('/chat', { state: { initialQuestion: question } });
    if (onQuestionClick) onQuestionClick();
  };

  return (
    <Card className={cn("border-white/10 bg-black/30 backdrop-blur-sm h-full flex flex-col", className)}>
      <CardHeader className="pb-2 pt-3">
        <div className="flex items-center gap-2">
          <BrainCircuit className="h-4 w-4 text-purple-400" />
          <CardTitle className="text-base font-bold text-white">Ask Strykr AI</CardTitle>
        </div>
        <CardDescription className="text-xs">
          Get quick answers to your market questions
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-0 pb-2">
        {isLoadingProfile ? (
          // Loading state with skeletons
          <div className="space-y-1.5 flex-1">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-full rounded-md bg-white/10" />
            ))}
          </div>
        ) : (
          // Carousel for questions
          <div className="relative flex-1 flex flex-col">
            <div className="space-y-1.5 flex-1">
              {visibleQuestions.map((question, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="w-full justify-between text-left h-auto py-1.5 px-2.5 border-white/10 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white"
                  onClick={() => handleQuestionClick(question)}
                >
                  <span className="flex-1 mr-2 line-clamp-1 text-xs">{question}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />
                </Button>
              ))}
            </div>
            
            {/* Carousel navigation */}
            <div className="flex justify-between mt-1.5">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 w-6 p-0 rounded-full bg-black/20 hover:bg-black/40"
                onClick={prevPage}
              >
                <ChevronLeft className="h-3 w-3 text-white/70" />
                <span className="sr-only">Previous questions</span>
              </Button>
              <div className="text-[10px] text-white/50">
                {currentPage + 1}/{Math.ceil(allQuestions.length / questionsPerPage)}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 w-6 p-0 rounded-full bg-black/20 hover:bg-black/40"
                onClick={nextPage}
              >
                <ChevronRight className="h-3 w-3 text-white/70" />
                <span className="sr-only">Next questions</span>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

AskStrykrAICard.displayName = 'AskStrykrAICard';

// Maintain original name export for compatibility if needed
export { AskStrykrAICard as SuggestedQuestionsCard }; 
export default AskStrykrAICard;
